// =======================
// src/componentes/ErrorWindow.js
// =======================

import React from "react";

import { Typography, Button, Box } from "@mui/material";

import { ErrorOutline, Refresh, CloudOff, Block } from "@mui/icons-material";

import { motion } from "framer-motion";

import TransicaoPagina from "./comuns/TransicaoPagina";

import { ajustarTamanhoFonteAdmin } from "../estilos/global";

const errorPresets = {
  network: {
    title: "Erro de Conexão",
    description:
      "Não foi possível se conectar ao servidor. Verifique sua internet e tente novamente.",
    icon: <CloudOff />,
  },

  permission: {
    title: "Acesso Negado",
    description: "Você não tem permissão para acessar esta página ou recurso.",
    icon: <Block />,
  },

  generic: {
    title: "Ocorreu um erro",
    description:
      "Não foi possível carregar os dados. Por favor, tente novamente mais tarde.",
    icon: <ErrorOutline />,
  },
};

const ErrorState = ({
  errorType = null,
  title,
  description,
  buttonText = "Recarregar Página",
  icon,
  showButton = true,
  onRetry,
  severity = "error",
}) => {
  const fallback = errorPresets[errorType] || errorPresets.generic;

  const finalTitle = title || fallback.title;
  const finalDescription = description || fallback.description;
  const finalIcon = icon || fallback.icon;

  const colorConfig = {
    error: {
      main: "#f44336",
      light: "rgba(244, 67, 54, 0.1)",
      gradient: "linear-gradient(90deg, #f44336, #ff5722)",
    },

    warning: {
      main: "#ff9800",
      light: "rgba(255, 152, 0, 0.1)",
      gradient: "linear-gradient(90deg, #ff9800, #ffc107)",
    },

    info: {
      main: "#2196f3",
      light: "rgba(33, 150, 243, 0.1)",
      gradient: "linear-gradient(90deg, #2196f3, #03a9f4)",
    },
  };

  const colors = colorConfig[severity] || colorConfig.error;

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      window.location.reload();
    }
  };

  return (
    <TransicaoPagina
      variante="navegacao"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        padding: "40px 20px",
        height: "100%",
        width: "100%",
      }}
    >
      <motion.div
        animate={{
          scale: [1, 1.05, 1],
          rotate: [0, -5, 5, 0],
        }}
        transition={{
          duration: 1.5,
          repeat: Infinity,
          repeatType: "reverse",
        }}
      >
        <Box
          sx={{
            width: 140,
            height: 140,
            borderRadius: "50%",
            background: colors.light,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 3,
            boxShadow: `0 8px 24px ${colors.light}`,
          }}
        >
          {React.cloneElement(finalIcon, {
            sx: {
              fontSize: 70,
              color: colors.main,
            },
          })}
        </Box>
      </motion.div>

      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          mb: 1.5,
          background: colors.gradient,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}
      >
        {finalTitle}
      </Typography>

      <Typography
        variant="body1"
        sx={{
          color: "text.secondary",
          mb: 4,
          maxWidth: "450px",
          fontSize: ajustarTamanhoFonteAdmin("1.1rem"),
        }}
      >
        {finalDescription}
      </Typography>

      {showButton && (
        <Button
          variant="contained"
          startIcon={<Refresh sx={{ fontSize: "1.5rem" }} />}
          onClick={handleRetry}
          sx={{
            borderRadius: "14px",
            px: 5,
            py: 1.8,
            textTransform: "none",
            fontSize: ajustarTamanhoFonteAdmin("1.1rem"),
            fontWeight: 600,
            background: colors.gradient,
            boxShadow: `0 8px 24px ${colors.light}`,

            "&:hover": {
              background: colors.gradient,
              opacity: 0.9,
            },
          }}
          component={motion.div}
          whileHover={{
            scale: 1.05,
            boxShadow: `0 12px 28px ${colors.light}`,
          }}
          whileTap={{ scale: 0.98 }}
        >
          {buttonText}
        </Button>
      )}
    </TransicaoPagina>
  );
};

export default ErrorState;
